import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../components/UI/Dialog";
import { X } from "lucide-react";
import { useNotification } from "../../../../hooks/useNotification";
import * as autoAssignApi from "../../../../api/autoAssignApi";

const AutoAssignSettingsModal = ({
  open,
  onOpenChange,
  accountId,
  staffs = [],
  shop = null,
}) => {
  const { addNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Local form state
  const [formData, setFormData] = useState({
    triggers: { group: false, stranger: false },
    assignmentMethod: "first_interaction",
    selectedStaffIds: [],
    autoAssignMode: "all_selected",
    timeoutEnabled: false,
    timeoutMinutes: 3,
    addOrTransfer: "transfer",
  });

  // Fetch config when modal opens
  useEffect(() => {
    const loadConfig = async () => {
      if (open && accountId) {
        setLoading(true);
        // Reset form to defaults or previous state while loading could be considered,
        // but here we want to ensure we don't show stale data if id changed.
        try {
          console.log("[AutoAssign] Fetching config for account:", accountId);
          const response = await autoAssignApi.getConfig(accountId);
          if (response.status === 1 && response.data) {
            const config = response.data;
            console.log("[AutoAssign] Config loaded:", config);

            // Force a fresh object for state update
            setFormData({
              triggers: config.triggers || { group: false, stranger: false },
              assignmentMethod: config.assignmentMethod || "first_interaction",
              selectedStaffIds: config.selectedStaffIds || [],
              autoAssignMode: config.autoAssignMode || "all_selected",
              timeoutEnabled: config.timeoutEnabled || false,
              timeoutMinutes: config.timeoutMinutes || 3,
              addOrTransfer: config.addOrTransfer || "transfer",
            });
          }
        } catch (error) {
          console.error("[AutoAssign] Load error:", error);
          addNotification("Không thể tải cấu hình", -1);
        } finally {
          setLoading(false);
        }
      }
    };

    if (open) {
      loadConfig();
    } else {
      // Optional: Reset form when closed if needed, but not strictly required if we reload on open
    }
  }, [open, accountId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log("[AutoAssign] Saving...", { accountId, ...formData });
      const response = await autoAssignApi.saveConfig({
        accountId,
        ...formData,
      });

      if (response.status === 1) {
        console.log("[AutoAssign] Save success!");
        addNotification("Lưu cấu hình thành công!", 1);
        onOpenChange(false);
      } else {
        throw new Error(response.message || "Lỗi không xác định");
      }
    } catch (error) {
      console.error("[AutoAssign] Save error:", error);
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message || "Có lỗi xảy ra khi lưu cấu hình";
      addNotification(errorMessage, -1);
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerChange = (trigger, checked) => {
    setFormData((prev) => ({
      ...prev,
      triggers: { ...prev.triggers, [trigger]: checked },
    }));
  };

  const handleStaffSelect = (staffId, checked) => {
    setFormData((prev) => ({
      ...prev,
      selectedStaffIds: checked
        ? [...prev.selectedStaffIds, staffId]
        : prev.selectedStaffIds.filter((id) => id !== staffId),
    }));
  };

  const isAutoAssign = formData.assignmentMethod === "auto_assign";

  // Get staff role display text
  const getStaffRoleText = (staff) => {
    // First check shopRoles if shop is available
    if (shop) {
      const shopRole = staff.shopRoles?.find((sr) => sr.shopId === shop._id);
      if (shopRole?.role?.includes("admin")) {
        return "(Quyền: Quản trị viên)";
      }
      if (shopRole?.role?.includes("editor")) {
        return "(Quyền: Biên tập viên)";
      }
    }
    // Fallback to global role
    const roles = staff.role || [];
    if (roles.includes("super_admin") || roles.includes("admin")) {
      return "(Quyền: Quản trị viên)";
    }
    if (roles.includes("editor")) {
      return "(Quyền: Biên tập viên)";
    }
    return "";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white text-black border border-gray-300 p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-300 bg-gray-50">
          <DialogTitle className="text-base font-semibold text-center flex-1">
            Chia data tự động
          </DialogTitle>
          <div
            className="cursor-pointer hover:bg-gray-200 p-1 rounded-full transition-colors"
            onClick={() => onOpenChange(false)}
          >
            <X className="w-4 h-4 text-gray-500" />
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center p-4">Loading...</div>
          ) : (
            <>
              {/* Section 1: Triggers */}
              <div className="space-y-2 border border-blue-100 bg-blue-50/50 p-3 rounded-sm">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Cơ chế chia data này áp dụng với:
                </div>
                <div className="space-y-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.triggers.group}
                      onChange={(e) =>
                        handleTriggerChange("group", e.target.checked)
                      }
                      className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-green-700">
                      Nhóm, khi tài khoản zalo được thêm mới vào nhóm
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.triggers.stranger}
                      onChange={(e) =>
                        handleTriggerChange("stranger", e.target.checked)
                      }
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-blue-700">
                      Người lạ: kết bạn hoặc gửi tin nhắn đầu tiên
                    </span>
                  </label>
                </div>
              </div>

              {/* Section 2: Assignment Method */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">
                  Cách chia
                </div>
                <div className="space-y-3 pl-1">
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="assignmentMethod"
                        value="first_interaction"
                        checked={
                          formData.assignmentMethod === "first_interaction"
                        }
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            assignmentMethod: e.target.value,
                          }))
                        }
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium">
                        Chia cho người tương tác đầu tiên (Mặc định)
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 ml-6 mt-1">
                      (Đồng ý kết bạn/gửi lời mời kết bạn đầu tiên/gửi tin nhắn
                      đầu tiên với người lạ đó)
                    </p>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="assignmentMethod"
                      value="auto_assign"
                      checked={formData.assignmentMethod === "auto_assign"}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          assignmentMethod: e.target.value,
                        }))
                      }
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium">
                      Chia data tự động
                    </span>
                  </label>
                </div>
              </div>

              {/* Section 3: Staff Selection */}
              <div
                className={`space-y-2 border border-gray-300 p-3 ${
                  !isAutoAssign ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                <div className="text-sm text-center border-b border-gray-300 pb-2 mb-2 font-medium">
                  Chọn danh sách được chia data tự động
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {staffs.length === 0 ? (
                    <p className="text-gray-500 text-sm">
                      Không có nhân viên nào
                    </p>
                  ) : (
                    staffs.map((staff) => (
                      <label
                        key={staff._id}
                        className="flex items-center gap-2 cursor-pointer py-1"
                      >
                        <input
                          type="checkbox"
                          checked={formData.selectedStaffIds.includes(
                            staff._id,
                          )}
                          onChange={(e) =>
                            handleStaffSelect(staff._id, e.target.checked)
                          }
                          disabled={!isAutoAssign}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span
                          className={`text-sm ${
                            formData.selectedStaffIds.includes(staff._id)
                              ? "text-green-600 font-medium"
                              : "text-gray-700"
                          }`}
                        >
                          {staff.name || staff.username}{" "}
                          {getStaffRoleText(staff)}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Section 4: Auto-assign Mechanism */}
              <div
                className={`space-y-2 border border-gray-300 p-3 ${
                  !isAutoAssign ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                <div className="text-sm text-center border-b border-gray-300 pb-2 mb-2 font-medium">
                  Cơ chế chia data tự động
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="autoAssignMode"
                      value="all_selected"
                      checked={formData.autoAssignMode === "all_selected"}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          autoAssignMode: e.target.value,
                        }))
                      }
                      disabled={!isAutoAssign}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm">
                      Chia đều cho tất cả nhân viên được chọn
                    </span>
                  </label>
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="autoAssignMode"
                        value="online_only"
                        checked={formData.autoAssignMode === "online_only"}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            autoAssignMode: e.target.value,
                          }))
                        }
                        disabled={!isAutoAssign}
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm">
                        Chia đều cho tất cả các nhân viên đang online được chọn
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 ml-6">
                      (Nếu không có nhân viên nào online, thì chia đều cho tất
                      cả các tài khoản được chọn)
                    </p>
                  </div>
                </div>

                {/* Timeout Settings */}
                <div className="pt-2 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.timeoutEnabled}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          timeoutEnabled: e.target.checked,
                        }))
                      }
                      disabled={!isAutoAssign}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm">
                      Nếu người được chia không phản hồi trong X phút thì chia
                      cho nhân viên tiếp theo
                    </span>
                  </label>

                  <div className="flex items-center gap-2 ml-6">
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={formData.timeoutMinutes}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          timeoutMinutes: parseInt(e.target.value) || 3,
                        }))
                      }
                      disabled={!isAutoAssign || !formData.timeoutEnabled}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm disabled:bg-gray-100"
                    />
                    <span className="text-sm">Phút</span>
                    <label className="flex items-center gap-1 cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={formData.addOrTransfer === "add"}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            addOrTransfer: e.target.checked
                              ? "add"
                              : "transfer",
                          }))
                        }
                        disabled={!isAutoAssign || !formData.timeoutEnabled}
                        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-600">
                        Add thêm nhân viên (Mặc định là chuyển quyền cho nhân
                        viên khác)
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Anti-spam note */}
              <div className="text-sm text-gray-600 pt-2">
                <span className="font-bold">Lưu ý:</span> Để tránh Zalo Spam,
                những hội thoại có người khác đã vào trả lời (đã có nhân viên
                được chia) sẽ <span className="font-bold">KHÔNG</span> chia data
                tự động nữa.
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-4 py-3 border-t border-gray-300">
          <button
            onClick={handleSave}
            disabled={loading || saving}
            className="px-6 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Đang lưu..." : "Lưu"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AutoAssignSettingsModal;
